import { useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  getOrdersState,
  getSuppliesWBWithOrders,
  IOrder,
  selectOrdersStore,
  updateSupplies,
} from "../../app/reducers/ordersReducer"
import s from "./Supplies.module.css"
import { FormXlsxForOrderIcon } from "../FormXlsxForOrderIcon/FormXlsxForOrderIcon"
import { FormPodrOrder } from "../FormPodrOrder/FormPodrOrder"
import { FormWBKatrenOrder } from "../FormKatrenOrder/wb/FormWBKatrenOrder"
import { FormWBSamsonOrder } from "../FormSamsonOrder/wb/FormWBSamsonOrder"
import { ExpToWbOrders } from "../FormKatrenOrder/wb/ExpToWbOrders"
import { MultiSelect } from "../Select/MultiSelect"
import { Button, Box } from "@radix-ui/themes"
import { DataTable, type DataTableColumn } from "../DataTable/DataTable"
import { selectActiveShops } from "../../app/reducers/settingsReducer"
import { SimalandCartButton } from "./SimalandCartButton"

const isValidDate = (value: unknown): boolean => {
  if (value == null) return false
  return !isNaN(new Date(value as string | number | Date).getTime())
}

const suppliesColumns: DataTableColumn<IOrder>[] = [
  { key: "id", header: "№ задания" },
  {
    key: "createdAt",
    header: "Создано",
    cell: (el) =>
      isValidDate(el.createdAt) ? new Date(el.createdAt).toLocaleString() : "—",
  },
  { key: "warehouseId", header: "Склад" },
  { key: "article", header: "Артикул продавца" },
  { key: "nmId", header: "Артикул WB" },
  {
    key: "convertedPrice",
    header: "Цена",
    cell: (el) =>
      Number.isFinite(+el.convertedPrice) ? +el.convertedPrice / 100 : "—",
  },
]

export const Supplies = () => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(getOrdersState())
  }, [dispatch])
  const ordersStore = useAppSelector(selectOrdersStore)
  const { supplies, lastDateUpdatedSupplies } = ordersStore
  const activeShops = useAppSelector(selectActiveShops)
  const { hasSamson, hasPodr, hasKatren, hasIcon, simalandShopIdent } =
    useMemo(() => {
      return {
        hasSamson: activeShops.some((el) => el.includes("SAMSON")),
        hasPodr: activeShops.some((el) => el.includes("PODRUZHKA")),
        hasKatren: activeShops.some((el) => el.includes("KATREN")),
        hasIcon: activeShops.some((el) => el.includes("ICON")),
        simalandShopIdent: activeShops.find((el) => el.includes("SIMALAND")),
      }
    }, [activeShops])
  const [selectedSupplyIds, setSelectedSupplyIds] = useState<string[]>([])
  const selectedSupplies = useMemo(
    () =>
      supplies.filter((supply) =>
        selectedSupplyIds.includes(String(supply.id)),
      ),
    [selectedSupplyIds, supplies],
  )
  const orderSupplies: IOrder[] = useMemo(
    () =>
      selectedSupplies?.reduce<IOrder[]>((res, supp) => {
        return [...res, ...(supp?.orders || [])]
      }, []),
    [selectedSupplies],
  )

  return (
    <Box px="4">
      <div>
        <h5>Формирование файла поставок для заказа у дистрьебьютера</h5>
      </div>
      <div>
        <div>
          <div className="flex flex-column flex-align-center gap-20">
            <div className="flex flex-align-justify gap-20">
              <MultiSelect
                values={selectedSupplyIds}
                options={supplies.map((supply) => ({
                  value: String(supply.id),
                  label: supply.name,
                }))}
                onChange={(values) => {
                  setSelectedSupplyIds(values)
                  values.forEach((supplyId) => {
                    const supp = supplies.find(
                      (el) => String(el.id) === supplyId,
                    )
                    if (!supp?.orders && supplyId) {
                      dispatch(getSuppliesWBWithOrders(String(supplyId)))
                    }
                  })
                }}
                placeholder="Выберите поставку"
              />
              <Button
                className={s.updateSupplies}
                onClick={() => {
                  dispatch(updateSupplies()).then(() =>
                    setSelectedSupplyIds([]),
                  )
                }}
              >
                Обновить поставки
              </Button>
            </div>
            <span>
              {lastDateUpdatedSupplies &&
                `(обновлено: ${lastDateUpdatedSupplies})`}
            </span>
          </div>
          <div>
            <div>
              <h5>Сборочные задания поставки</h5>
              <div className={s.content}>
                <DataTable<IOrder>
                  columns={suppliesColumns}
                  data={orderSupplies}
                  rowKey={(el, i) => el.article + "-order-supp-" + i}
                />
                <div className={s.buttonsWrap}>
                  {(hasKatren && (
                    <div
                      className="flex flex-column gap-10"
                      style={{ background: "#f3f3f3" }}
                    >
                      <strong>Катрен</strong>
                      <FormWBKatrenOrder
                        name="Сформировать файл для заказа"
                        selectedSupplies={selectedSupplies}
                      />
                      <ExpToWbOrders orders={orderSupplies} />
                    </div>
                  )) ||
                    null}
                  {(hasSamson && (
                    <FormWBSamsonOrder selectedSupplies={selectedSupplies} />
                  )) ||
                    null}
                  {(hasIcon && (
                    <FormXlsxForOrderIcon orders={orderSupplies} />
                  )) ||
                    null}
                  {(hasPodr && (
                    <FormPodrOrder
                      supplyId={
                        selectedSupplyIds?.[0] != null
                          ? String(selectedSupplyIds[0])
                          : undefined
                      }
                    />
                  )) ||
                    null}
                  {(simalandShopIdent && (
                    <SimalandCartButton
                      shopIdent={simalandShopIdent}
                      selectedSupplyIds={selectedSupplyIds}
                    />
                  )) ||
                    null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Box>
  )
}
